/**
 * Session Backup Store - IndexedDB-based session persistence
 * 
 * Provides a backup layer for Supabase auth sessions that survives
 * aggressive Android WebView storage purges. IndexedDB is generally
 * more persistent than localStorage on mobile PWAs.
 * 
 * This is NOT a replacement for Supabase's built-in session management,
 * but a fallback for when localStorage gets cleared by the OS.
 */

import { Session } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';

const DB_NAME = 'lupuslogix-auth';
const DB_VERSION = 1;
const STORE_NAME = 'session';
const SESSION_KEY = 'current_session';

// Session backup expires after 30 days (matches Supabase refresh token lifetime)
const SESSION_BACKUP_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;

interface SessionBackup {
  key: string;
  session: Session;
  backedUpAt: number;
  rememberMe: boolean;
}

class SessionBackupStore {
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Initialize the IndexedDB database for session backup
   */
  private async initDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        logger.error('[SessionBackup] Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      };
    });

    return this.dbPromise;
  }

  /**
   * Backup a session to IndexedDB
   */
  async backupSession(session: Session, rememberMe: boolean = true): Promise<void> {
    if (!rememberMe) {
      // If "Remember me" is not checked, don't backup to IndexedDB
      await this.clearBackup();
      return;
    }

    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const backup: SessionBackup = {
        key: SESSION_KEY,
        session,
        backedUpAt: Date.now(),
        rememberMe,
      };

      await new Promise<void>((resolve, reject) => {
        const request = store.put(backup);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      if (import.meta.env.DEV) {
        logger.log('[SessionBackup] Session backed up to IndexedDB');
      }
    } catch (error) {
      logger.error('[SessionBackup] Failed to backup session:', error);
    }
  }

  /**
   * Restore a session from IndexedDB backup
   * Returns null if no valid backup exists
   */
  async restoreSession(): Promise<Session | null> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      const backup = await new Promise<SessionBackup | undefined>((resolve, reject) => {
        const request = store.get(SESSION_KEY);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (!backup) {
        if (import.meta.env.DEV) {
          logger.log('[SessionBackup] No backup found in IndexedDB');
        }
        return null;
      }

      // Check if backup is expired
      const age = Date.now() - backup.backedUpAt;
      if (age > SESSION_BACKUP_EXPIRY_MS) {
        if (import.meta.env.DEV) {
          logger.log('[SessionBackup] Backup expired, clearing');
        }
        await this.clearBackup();
        return null;
      }

      // Check if the session's refresh token is still valid
      // (Supabase refresh tokens typically expire after 30 days)
      const expiresAt = backup.session.expires_at;
      if (expiresAt && expiresAt * 1000 < Date.now()) {
        // Access token expired, but refresh token might still work
        // Return the session so Supabase can try to refresh it
        if (import.meta.env.DEV) {
          logger.log('[SessionBackup] Access token expired, returning for refresh attempt');
        }
      }

      if (import.meta.env.DEV) {
        logger.log('[SessionBackup] Session restored from IndexedDB backup');
      }

      return backup.session;
    } catch (error) {
      logger.error('[SessionBackup] Failed to restore session:', error);
      return null;
    }
  }

  /**
   * Clear the session backup
   */
  async clearBackup(): Promise<void> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.delete(SESSION_KEY);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      if (import.meta.env.DEV) {
        logger.log('[SessionBackup] Session backup cleared');
      }
    } catch (error) {
      logger.error('[SessionBackup] Failed to clear backup:', error);
    }
  }

  /**
   * Check if a backup exists (without retrieving it)
   */
  async hasBackup(): Promise<boolean> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      const backup = await new Promise<SessionBackup | undefined>((resolve, reject) => {
        const request = store.get(SESSION_KEY);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (!backup) return false;

      // Check if expired
      const age = Date.now() - backup.backedUpAt;
      return age <= SESSION_BACKUP_EXPIRY_MS;
    } catch {
      return false;
    }
  }

  /**
   * Get "Remember me" preference from last session
   */
  async getRememberMePreference(): Promise<boolean> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      const backup = await new Promise<SessionBackup | undefined>((resolve, reject) => {
        const request = store.get(SESSION_KEY);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      return backup?.rememberMe ?? true;
    } catch {
      return true;
    }
  }
}

export const sessionBackupStore = new SessionBackupStore();
